import React, { useState } from "react";
import { api, formatApiErrorDetail } from "@/lib/api";
import { useStore } from "@/context/StoreContext";
import { toast } from "sonner";
import { Phone, MessageCircle, Mail, MapPin, Clock, Send, Loader2 } from "lucide-react";

export default function Contact() {
  const { settings } = useStore();
  const [form, setForm] = useState({ name: "", phone: "", email: "", subject: "", message: "" });
  const [submitting, setSubmitting] = useState(false);

  const set = (k) => (e) => setForm({ ...form, [k]: e.target.value });

  const submit = async (e) => {
    e.preventDefault();
    if (!form.name || !form.phone || !form.message) {
      toast.error("Please fill in your name, phone and message.");
      return;
    }
    setSubmitting(true);
    try {
      await api.post("/enquiries", form);
      toast.success("Thank you! We've received your enquiry and will be in touch.");
      setForm({ name: "", phone: "", email: "", subject: "", message: "" });
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail) || "Could not send. Please try again.");
    } finally {
      setSubmitting(false);
    }
  };

  const whatsappNumber = (settings?.whatsapp || "").replace(/[^0-9]/g, "");

  const contactItems = [
    settings?.phone && { icon: Phone, label: "Call us", value: settings.phone, href: `tel:${settings.phone}` },
    whatsappNumber && { icon: MessageCircle, label: "WhatsApp", value: settings.whatsapp, href: `https://wa.me/${whatsappNumber}` },
    settings?.email && { icon: Mail, label: "Email", value: settings.email, href: `mailto:${settings.email}` },
    settings?.address && { icon: MapPin, label: "Visit us", value: settings.address },
    settings?.hours && { icon: Clock, label: "Opening hours", value: settings.hours },
  ].filter(Boolean);

  return (
    <div data-testid="contact-page" className="max-w-7xl mx-auto px-4 sm:px-6 py-12 md:py-16">
      <div className="mb-12">
        <h1 className="font-display text-4xl sm:text-5xl font-black tracking-tighter text-brand-blue">Get in touch</h1>
        <p className="mt-3 text-brand-black/60 max-w-xl">Have a question or want to place an order? Call, WhatsApp or send us a message below.</p>
      </div>

      <div className="grid lg:grid-cols-2 gap-10">
        <div className="space-y-4">
          {contactItems.map((c, i) => {
            const Wrapper = c.href ? "a" : "div";
            return (
              <Wrapper
                key={i}
                {...(c.href ? { href: c.href, target: c.href.startsWith("http") ? "_blank" : undefined, rel: "noopener noreferrer" } : {})}
                data-testid={`contact-${c.label.toLowerCase().replace(/\s/g, "-")}`}
                className="flex items-start gap-4 bg-white border-2 border-black rounded-lg p-5 shadow-brutal hover:-translate-y-1 hover:shadow-brutal-lg transition-all duration-200"
              >
                <span className="w-11 h-11 shrink-0 flex items-center justify-center bg-brand-gold border-2 border-black rounded-md">
                  <c.icon className="w-5 h-5 text-brand-blue" strokeWidth={2.5} />
                </span>
                <div>
                  <p className="font-display font-bold text-brand-blue">{c.label}</p>
                  <p className="text-brand-black/70">{c.value}</p>
                </div>
              </Wrapper>
            );
          })}
        </div>

        <form onSubmit={submit} data-testid="enquiry-form" className="bg-brand-cream border-2 border-black rounded-lg shadow-brutal-lg p-6 md:p-8 space-y-4">
          <h2 className="font-display font-bold text-2xl text-brand-blue">Send an enquiry</h2>
          <div className="grid sm:grid-cols-2 gap-4">
            <div>
              <label className="block text-sm font-semibold mb-1">Name *</label>
              <input data-testid="enquiry-name" value={form.name} onChange={set("name")} className="w-full px-4 py-3 border-2 border-black rounded-md bg-white focus:outline-none focus:ring-2 focus:ring-brand-gold" />
            </div>
            <div>
              <label className="block text-sm font-semibold mb-1">Phone *</label>
              <input data-testid="enquiry-phone" value={form.phone} onChange={set("phone")} className="w-full px-4 py-3 border-2 border-black rounded-md bg-white focus:outline-none focus:ring-2 focus:ring-brand-gold" />
            </div>
          </div>
          <div className="grid sm:grid-cols-2 gap-4">
            <div>
              <label className="block text-sm font-semibold mb-1">Email</label>
              <input data-testid="enquiry-email" value={form.email} onChange={set("email")} className="w-full px-4 py-3 border-2 border-black rounded-md bg-white focus:outline-none focus:ring-2 focus:ring-brand-gold" />
            </div>
            <div>
              <label className="block text-sm font-semibold mb-1">Subject</label>
              <input data-testid="enquiry-subject" value={form.subject} onChange={set("subject")} className="w-full px-4 py-3 border-2 border-black rounded-md bg-white focus:outline-none focus:ring-2 focus:ring-brand-gold" />
            </div>
          </div>
          <div>
            <label className="block text-sm font-semibold mb-1">Message *</label>
            <textarea data-testid="enquiry-message" value={form.message} onChange={set("message")} rows={5} className="w-full px-4 py-3 border-2 border-black rounded-md bg-white focus:outline-none focus:ring-2 focus:ring-brand-gold resize-none" />
          </div>
          <button type="submit" disabled={submitting} data-testid="enquiry-submit" className="inline-flex items-center gap-2 bg-brand-blue text-white font-semibold px-6 py-3 border-2 border-black rounded-md shadow-brutal hover:-translate-y-0.5 active:translate-y-0.5 transition-all disabled:opacity-60">
            {submitting ? <Loader2 className="w-5 h-5 animate-spin" /> : <Send className="w-5 h-5" />} Send enquiry
          </button>
        </form>
      </div>
    </div>
  );
}
